import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { Plus } from 'lucide-react'

import React, { useState } from 'react'

import { FieldSchema } from '@/lib/builder/schemas'

import { Button } from '@/components/ui/button'

import { SortableItem } from './SortableItem'

interface ArrayFieldProps {
  field: FieldSchema
  value: any[]
  onChange: (value: any[]) => void
  onImageRequest: (path: string) => void
  path: string
  renderField: (
    field: FieldSchema,
    value: any,
    onChange: (val: any) => void,
    path: string
  ) => React.ReactNode
}

export const ArrayField = ({
  field,
  value,
  onChange,
  onImageRequest,
  path,
  renderField,
}: ArrayFieldProps) => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleToggle = (idx: number) => {
    setExpandedIndex(expandedIndex === idx ? null : idx)
  }

  const handleRemove = (e: React.MouseEvent, idx: number) => {
    e.stopPropagation()
    const newValue = [...(value || [])]
    newValue.splice(idx, 1)
    onChange(newValue)
    if (expandedIndex === idx) setExpandedIndex(null)
  }

  const handleAdd = () => {
    const newItem =
      field.itemSchema?.reduce((acc: any, f: any) => {
        acc[f.key] = f.default || (f.type === 'number' ? 0 : f.type === 'boolean' ? false : '')
        return acc
      }, {}) || {}
    const newValue = [...(value || []), newItem]
    onChange(newValue)
    setExpandedIndex(newValue.length - 1)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (active.id !== over?.id) {
      const oldIndex = parseInt(active.id.toString())
      const newIndex = parseInt(over!.id.toString())
      onChange(arrayMove(value, oldIndex, newIndex))

      // Adjust expanded index if moving
      if (expandedIndex === oldIndex) setExpandedIndex(newIndex)
      else if (expandedIndex !== null) {
        if (oldIndex < expandedIndex && newIndex >= expandedIndex)
          setExpandedIndex(expandedIndex - 1)
        else if (oldIndex > expandedIndex && newIndex <= expandedIndex)
          setExpandedIndex(expandedIndex + 1)
      }
    }
  }

  const items = (value || []).map((_: any, idx: number) => idx.toString())

  return (
    <div className="space-y-4">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={items} strategy={verticalListSortingStrategy}>
          <div className="space-y-4">
            {(value || []).map((item: any, idx: number) => (
              <SortableItem
                key={idx}
                idx={idx}
                item={item}
                field={field}
                value={value}
                expandedIndex={expandedIndex}
                handleToggle={handleToggle}
                handleRemove={handleRemove}
                onChange={onChange}
                onImageRequest={onImageRequest}
                path={path}
                renderField={renderField}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <Button
        variant="outline"
        className="w-full h-12 border-dashed border-2 hover:bg-primary/5 hover:border-primary/50 transition-all rounded-xl"
        onClick={handleAdd}>
        <Plus className="h-4 w-4 mr-2" /> Add {field.label ? field.label.replace(/s$/, '') : 'Item'}
      </Button>
    </div>
  )
}
